<!-- Vitals_History.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">Vitals</h3>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-teal-50 border-b border-teal-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-teal-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-teal-800 leading-relaxed">{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="vitals.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'heart-pulse']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No vital signs data found</p>
    </div>

    <div v-else class="p-4">
      <!-- Vital type pills -->
      <div class="flex flex-wrap gap-1.5 mb-3">
        <button
          @click="selectedType = null"
          :class="[
            'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
            !selectedType
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
          ]"
        >
          All ({{ vitals.length }})
        </button>
        <button
          v-for="t in vitalTypes"
          :key="t.name"
          @click="selectedType = t.name"
          :class="[
            'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
            selectedType === t.name
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
          ]"
        >
          {{ t.name }} ({{ t.count }})
        </button>
      </div>

      <!-- Vitals table -->
      <div class="space-y-1">
        <div
          v-for="(v, i) in filteredVitals"
          :key="i"
          class="flex items-center gap-2 text-xs py-1.5 border-b border-gray-50 last:border-0"
        >
          <span class="text-gray-600 font-medium w-28 flex-shrink-0 truncate">
            {{ v.CODE_DISPLAY || v.CODE || 'Unknown' }}
          </span>
          <span class="font-medium" :class="v.ABNORMAL_FLAG ? 'text-red-600' : 'text-gray-800'">
            {{ v.VALUE_STRING || v.VALUE_NUMERIC || '—' }}
            <span v-if="v.UNITS" class="text-gray-400 font-normal ml-0.5">{{ v.UNITS }}</span>
          </span>
          <FontAwesomeIcon v-if="v.ABNORMAL_FLAG" :icon="['fas', 'exclamation-circle']" class="text-[9px] text-red-400" />
          <span v-if="v.SERVICE_DATE" class="text-[10px] text-gray-400 ml-auto">{{ formatDate(v.SERVICE_DATE) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Vitals_History',
  components: { FontAwesomeIcon },
  props: {
    observations: { type: Array, default: () => [] },
    encounters: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      selectedType: null,
    };
  },
  computed: {
    vitals() {
      // Filter observations to vitals category only
      const vitalObs = this.observations.filter(o =>
        (o.CATEGORY || '').toLowerCase() === 'vitals' ||
        (o.CATEGORY || '').toLowerCase() === 'vital-signs'
      );
      // Also collect vitals from encounter-nested observations
      for (const enc of this.encounters) {
        for (const o of (enc.observations || [])) {
          const cat = (o.CATEGORY || '').toLowerCase();
          if (cat === 'vitals' || cat === 'vital-signs') {
            vitalObs.push({ ...o, _encounter_date: enc.ENCOUNTER_DATE });
          }
        }
      }
      return vitalObs.sort((a, b) =>
        (b.SERVICE_DATE || '').localeCompare(a.SERVICE_DATE || '')
      );
    },
    vitalTypes() {
      const counts = {};
      for (const v of this.vitals) {
        const name = v.CODE_DISPLAY || v.CODE || 'Unknown';
        counts[name] = (counts[name] || 0) + 1;
      }
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    },
    filteredVitals() {
      if (!this.selectedType) return this.vitals;
      return this.vitals.filter(v =>
        (v.CODE_DISPLAY || v.CODE || 'Unknown') === this.selectedType
      );
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>
